import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { useAppSettingsData } from '../../Shared/Providers/AppSettingsProvider';
import useOutsideClick from '../../Shared/Hooks/useOutsideClick';
import { useKexNavigate } from '../../Kex/KexRouter/KexRouter';
import { styled } from '../../stitches.config';
import {
  EventDispatcher,
  ON_MOBILE_QUICKSEARCH_TOGGLE,
} from '../../Shared/Common/EventDispatcher';
import { useTranslationData } from '../../Shared/Providers/TranslationProvider';

function MobileQuickSearch() {
  const {
    staticPages: { searchPage },
  } = useAppSettingsData();

  const {
    'common/search': searchButtonText,
    'search/mobilePlaceholder': placeholderText,
  } = useTranslationData();

  const kexNavigate = useKexNavigate();
  const [isOpen, setOpen] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useOutsideClick(wrapperRef, () => {
    isOpen && onToggle();
  });

  const onToggle = () => {
    if (!isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
    setOpen(!isOpen);
  };

  const doSearch = () => {
    if (query.length > 0) {
      kexNavigate(`${searchPage}?searchQuery=${query}`);
      onToggle();
    }
  };

  useEffect(() => {
    EventDispatcher.subscribe(ON_MOBILE_QUICKSEARCH_TOGGLE, onToggle);

    return () => {
      EventDispatcher.unsubscribe(ON_MOBILE_QUICKSEARCH_TOGGLE, onToggle);
    };
  });

  return (
    <Wrapper ref={wrapperRef} transform={isOpen}>
      {isOpen && (
        <Container>
          <InputField
            onChange={(e: React.FormEvent<HTMLInputElement>) =>
              setQuery(e.currentTarget.value)
            }
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
              e.key === 'Enter' && doSearch()
            }
            ref={inputRef}
            defaultValue={query}
            type="search"
            placeholder={placeholderText}
          />
          <SearchButton onClick={doSearch}>{searchButtonText}</SearchButton>
        </Container>
      )}
    </Wrapper>
  );
}

const Wrapper = styled('div', {
  position: 'absolute',
  l: 0,
  t: '100%',
  backgroundColor: '$mobileSearchBackgroundPrimary',
  w: '100%',
  zIndex: '$FlyoutLower',
  transition: 'transform $300',
  boxShadow: '0 6px 6px -6px rgba(72, 72, 72, 0.3)',
  variants: {
    transform: {
      true: {
        p: 4,
        transform: 'translateY(0)',
      },
      false: {
        transform: 'translateY(-100%)',
      },
    },
  },
});

const Container = styled('div', {
  display: 'flex',
  alignItems: 'center',
  t: '50%',
  l: '50%',
  w: '100%',
  borderBottom: '1px solid $searchInputBorderPrimary',
  pb: 2,
});

const InputField = styled('input', {
  backgroundColor: 'transparent',
  color: '$grey700',
  flexGrow: 1,
  flexShrink: 1,
  fs: 8,
  pl: 2,
  letterSpacing: '1px',
  '&:focus': {
    color: '$searchInputTextPrimary',
    outline: 'none',
  },
  '&::placeholder': {
    color: '$grey700',
    fs: 6,
  },
});

const SearchButton = styled('button', {
  backgroundColor: 'transparent',
  mx: 2,
  fs: 8,
  fontWeight: '$fw300',
  borderWidth: 0,
  '&:focus': {
    outline: 'none',
  },
});

export default styled(MobileQuickSearch);
